import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { devices, loxone, type Zariadenie } from '../api/client.js';
import { useApi } from '../api/use-api.js';
import {
  Badge,
  Button,
  Card,
  DataRow,
  Notice,
  PageHeader,
  SectionTitle,
} from '../components/ui.js';
import { IconChevronRight, IconPencil, IconTrash } from '../components/icons.js';
import { useSession } from '../auth/session.js';
import { Data, nazovSchopnosti, popisDostupnosti, tonDostupnosti } from './common.js';
import { Merania, PanelOvladania, Stavy, maStavy, useOvladanie } from './controls.js';

/*
 * Detail zariadenia.
 *
 * Poradie je podla toho, ako casto to clovek potrebuje: najprv ovladanie,
 * potom merania, az potom technicke udaje a mapovania do Loxone.
 */

function Premenovanie({
  device,
  poZmene,
}: {
  readonly device: Zariadenie;
  readonly poZmene: () => void;
}): ReactNode {
  const [upravuje, setUpravuje] = useState(false);
  const [nazov, setNazov] = useState(device.name);
  const [chyba, setChyba] = useState<string | undefined>(undefined);

  if (!upravuje) {
    return (
      <Button
        variant="ghost"
        icon={<IconPencil className="size-4" />}
        onClick={() => setUpravuje(true)}
      >
        Premenovať
      </Button>
    );
  }

  const uloz = (event: FormEvent) => {
    event.preventDefault();
    setChyba(undefined);
    void devices
      .rename(device.id, nazov)
      .then(() => {
        setUpravuje(false);
        poZmene();
      })
      .catch((error: unknown) => {
        setChyba(error instanceof Error ? error.message : 'Premenovanie zlyhalo.');
      });
  };

  return (
    <form onSubmit={uloz} className="flex flex-wrap items-center gap-2">
      <input
        value={nazov}
        onChange={(e) => setNazov(e.target.value)}
        aria-label="Nový názov zariadenia"
        className="rounded-(--radius-control) border border-(--color-border-strong) bg-(--color-surface) px-3 py-2 text-sm text-(--color-text)"
      />
      <Button type="submit" variant="primary">
        Uložiť
      </Button>
      <Button
        variant="ghost"
        onClick={() => {
          setNazov(device.name);
          setUpravuje(false);
        }}
      >
        Zrušiť
      </Button>
      {chyba !== undefined && <Notice tone="danger">{chyba}</Notice>}
    </form>
  );
}

function Mapovania({ deviceId }: { readonly deviceId: string }): ReactNode {
  const nacitanie = useApi((signal) => loxone.mappings(signal), [deviceId]);
  const moje = (nacitanie.data?.mappings ?? []).filter((m) => m.deviceId === deviceId);

  if (nacitanie.loading || nacitanie.error !== undefined) return null;

  return (
    <section className="flex flex-col gap-3">
      <SectionTitle>V Loxone</SectionTitle>
      {moje.length === 0 ? (
        <Card className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
          <p className="text-sm text-(--color-text-muted)">
            Zariadenie zatiaľ nie je priradené žiadnemu Miniserveru.
          </p>
          <Link
            to="/loxone"
            className="inline-flex items-center gap-1 text-sm font-medium text-(--color-accent)"
          >
            Priradiť
            <IconChevronRight className="size-4" />
          </Link>
        </Card>
      ) : (
        <ul className="flex flex-col gap-2">
          {moje.map((m) => (
            <Card as="li" key={m.id} className="flex items-center justify-between gap-3 px-4 py-3">
              <span className="min-w-0 truncate font-mono text-sm text-(--color-text)">
                {m.virtualInputName}
              </span>
              <Badge tone="neutral">
                {m.transport === 'udp-batch' ? 'dávkové UDP' : 'HTTP polling'}
              </Badge>
            </Card>
          ))}
        </ul>
      )}
    </section>
  );
}

function Obsah({
  device,
  reload,
}: {
  readonly device: Zariadenie;
  readonly reload: () => void;
}): ReactNode {
  const { user } = useSession();
  const ovladanie = useOvladanie(device, reload);
  const jeSpravca = user?.role === 'admin';

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={device.name}
        detail={[device.manufacturer, device.model].filter(Boolean).join(' ') || undefined}
        action={jeSpravca ? <Premenovanie device={device} poZmene={reload} /> : undefined}
      />

      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={tonDostupnosti(device.availability)}>
          {popisDostupnosti(device.availability)}
        </Badge>
        <Badge tone="neutral">{device.integrationId}</Badge>
        {/*
          Cas plati pri KAZDOM stave dostupnosti. Pri „neznamom" je to
          jediny udaj, z ktoreho sa da povedat, ci zariadenie zije -
          a pri „nedostupnom" hovori, odkedy mlci.
        */}
        <span className="text-xs text-(--color-text-subtle)">
          {device.lastMessageAt === null
            ? 'zatiaľ nič neposlalo'
            : `naposledy ${casSpravy(device.lastMessageAt)}`}
        </span>
      </div>

      {jeSpravca ? (
        <Card className="flex flex-col gap-4 p-4 sm:p-5">
          <SectionTitle>Ovládanie</SectionTitle>
          <PanelOvladania device={device} ovladanie={ovladanie} />
        </Card>
      ) : (
        <Notice tone="warn">Na ovládanie zariadenia potrebuješ rolu správcu.</Notice>
      )}

      <StavyKarta device={device} />

      <Card className="flex flex-col gap-4 p-4 sm:p-5">
        <SectionTitle>Merania</SectionTitle>
        <Merania device={device} />
      </Card>

      <Gesta device={device} />

      <Mapovania deviceId={device.id} />

      <section className="flex flex-col gap-3">
        <SectionTitle>Technické údaje</SectionTitle>
        <Card className="px-4 py-1">
          <dl>
            <DataRow label="Identifikátor">
              <span className="font-mono text-xs break-all">{device.id}</span>
            </DataRow>
            <DataRow label="Kľúč integrácie">
              <span className="font-mono text-xs break-all">{device.nativeId}</span>
            </DataRow>
            <DataRow label="Profil">{device.profileId ?? 'bez profilu'}</DataRow>
            <DataRow label="Schopnosti">
              {device.capabilities.map((c) => nazovSchopnosti(c.name)).join(', ')}
            </DataRow>
            {/*
             * Rozsah, ktory hlasi samotne zariadenie. Je to odpoved na
             * otazku „preco sa svetlo nad 4000 K uz nemeni" - a rovnake
             * cisla patria do minima a maxima v Lighting Controlleri.
             */}
            {device.capabilities
              .filter((c) => c.range !== undefined)
              .map((c) => (
                <DataRow key={`rozsah-${c.name}`} label={`Rozsah — ${nazovSchopnosti(c.name)}`}>
                  {c.range?.min} až {c.range?.max}
                  {c.name === 'ColorTemperature' ? ' K' : ''}
                </DataRow>
              ))}
          </dl>
        </Card>
        {jeSpravca && <Zmazanie device={device} />}
      </section>
    </div>
  );
}

/**
 * Zmazanie zariadenia z modelu.
 *
 * Pre zariadenie, ktore uz neexistuje - preparovane inam, vymenene,
 * vyhodene. Bez toho zostavalo v prehlade navzdy: integracia ho uz
 * nehlasi, takze ho nema co odstranit.
 */
function Zmazanie({ device }: { readonly device: Zariadenie }): ReactNode {
  const navigate = useNavigate();
  const [pyta, setPyta] = useState(false);
  const [maze, setMaze] = useState(false);
  const [chyba, setChyba] = useState<string | undefined>(undefined);

  const zmaz = (): void => {
    setChyba(undefined);
    setMaze(true);
    void devices
      .remove(device.id)
      .then(() => {
        void navigate('/zariadenia');
      })
      .catch((error: unknown) => {
        setChyba(error instanceof Error ? error.message : 'Zariadenie sa nepodarilo zmazať.');
        setMaze(false);
      });
  };

  return (
    <div className="flex flex-col gap-2">
      {chyba !== undefined && <Notice tone="danger">{chyba}</Notice>}

      {pyta ? (
        <>
          <Notice tone="warn">
            <p className="font-medium">Zmazať {device.name}?</p>
            <p className="mt-1">
              Zmizne z prehľadu aj z mapovaní do Loxone; objekty, ktoré už v Miniserveri sú, tam
              zostanú. Pokiaľ zariadenie stále vysiela, pri najbližšom ohlásení sa objaví znova —
              tentoraz bez nastavení.
            </p>
          </Notice>
          <div className="flex flex-wrap gap-2">
            <Button variant="danger" onClick={zmaz} disabled={maze}>
              {maze ? 'Mažem…' : 'Naozaj zmazať'}
            </Button>
            <Button variant="ghost" onClick={() => setPyta(false)} disabled={maze}>
              Nechať
            </Button>
          </div>
        </>
      ) : (
        <div>
          <Button
            variant="danger"
            icon={<IconTrash className="size-4" />}
            onClick={() => setPyta(true)}
          >
            Zmazať zariadenie
          </Button>
        </div>
      )}
    </div>
  );
}

/** Ako casto sa detail sam obnovuje, kym je otvoreny. */
const INTERVAL_DETAILU_MS = 5000;

export function DeviceDetail(): ReactNode {
  const { deviceId } = useParams();
  const nacitanie = useApi(() => devices.get(deviceId ?? ''), [deviceId]);

  /*
   * Detail sa obnovuje POTICHU. `reload` by pri kazdom kole schoval
   * obsah za stav nacitavania, ovladacie prvky by sa odpojili a znova
   * pripojili - a prave preto sa hodnota po prvom kliknuti vracala
   * spat na povodnu.
   */
  useEffect(() => {
    const timer = setInterval(() => {
      nacitanie.refresh();
    }, INTERVAL_DETAILU_MS);
    return () => clearInterval(timer);
  }, [nacitanie.refresh]);

  return (
    <Data
      nacitanie={nacitanie}
      prazdne={{ title: 'Zariadenie sa nenašlo', detail: 'Možno bolo medzitým odobrané.' }}
    >
      {(data) => <Obsah device={data.device} reload={nacitanie.refresh} />}
    </Data>
  );
}

/**
 * Cas poslednej spravy.
 *
 * Do minuty „pred chvilou", potom relativne, a nad den uz s datumom -
 * „pred 40320 minutami" nikomu nic nepovie.
 */
function casSpravy(iso: string): string {
  const kedy = new Date(iso);
  const sekundy = Math.round((Date.now() - kedy.getTime()) / 1000);

  if (!Number.isFinite(sekundy)) return iso;
  if (sekundy < 60) return 'pred chvíľou';
  if (sekundy < 3600)
    return `pred ${Math.floor(sekundy / 60)} min · ${kedy.toLocaleTimeString('sk-SK')}`;
  if (sekundy < 86400)
    return `pred ${Math.floor(sekundy / 3600)} h · ${kedy.toLocaleTimeString('sk-SK')}`;
  return kedy.toLocaleString('sk-SK');
}

/**
 * Gestá tlačidla.
 *
 * Zoznam hlási samo zariadenie. Ukazuje sa preto, že rozhranie
 * Zigbee2MQTT je viazané len na slučku — toto je jediné miesto, kde si
 * používateľ prečíta, čo jeho ovládač vie a aké objekty v Loxone
 * z neho vzniknú.
 */
function Gesta({ device }: { readonly device: Zariadenie }): ReactNode {
  const akcia = device.capabilities.find((c) => c.name === 'Action');
  if (akcia?.values === undefined || akcia.values.length === 0) return null;

  return (
    <section className="flex flex-col gap-3">
      <SectionTitle>Gestá tlačidla ({akcia.values.length})</SectionTitle>
      <Card className="flex flex-col gap-3 p-4 sm:p-5">
        <div className="flex flex-wrap gap-2">
          {akcia.values.map((g) => (
            <Badge key={g} tone="neutral">
              {g}
            </Badge>
          ))}
        </div>
        <p className="text-xs text-(--color-text-muted)">
          Každé gesto dostane v Loxone vlastný virtuálny vstup, ktorý pri stlačení pulzne — dá sa
          priviesť rovno na vstup bloku. K tomu je jeden analógový vstup s číslom gesta v poradí
          vyššie, pre porovnávač.
        </p>
      </Card>
    </section>
  );
}

/**
 * Stav senzorov a tlačidiel.
 *
 * Karta sa neukazuje prázdna: zariadenie, ktoré nič takéto nehlási, ju
 * nedostane vôbec.
 */
function StavyKarta({ device }: { readonly device: Zariadenie }): ReactNode {
  if (!maStavy(device)) return null;

  return (
    <Card className="flex flex-col gap-4 p-4 sm:p-5">
      <SectionTitle>Stav</SectionTitle>
      <Stavy device={device} />
    </Card>
  );
}
